import React, { useState, useEffect } from 'react';
import { ImageOff } from 'lucide-react';

/**
 * Resolves a creative URL to a renderable image URL.
 * - `/uploads/...` → prepends REACT_APP_BACKEND_URL
 * - Google Drive `/file/d/ID/...` or `?id=ID` → direct view URL
 * - Anything else (http/https) → returned unchanged
 */
export const resolveCreativeUrl = (url) => {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  // Backend-relative paths: GridFS-backed `/api/files/...` or legacy `/uploads/...`
  if (trimmed.startsWith('/api/files/') || trimmed.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${trimmed}`;
  }
  const driveMatch = trimmed.match(
    /drive\.google\.com\/.*?(?:\/file\/d\/|[?&]id=)([\w-]{20,})/
  );
  if (driveMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }
  return trimmed;
};

/**
 * Renders a campaign creative without cropping. Any dimension / any ratio.
 * Falls back to a styled placeholder if the URL is missing or fails to load.
 *
 * Props:
 *  - src: raw creative URL stored on the campaign
 *  - alt: accessible label
 *  - className: optional extra classes for the wrapper
 *  - minHeight: tailwind class for min height (default `min-h-[180px]`)
 *  - rounded: tailwind rounded class (default `rounded-xl`)
 */
const CreativeImage = ({
  src,
  alt = 'Campaign creative',
  className = '',
  minHeight = 'min-h-[180px]',
  rounded = 'rounded-xl',
}) => {
  const resolved = resolveCreativeUrl(src);
  const [broken, setBroken] = useState(false);

  // Reset broken state when src changes (e.g. after replace)
  useEffect(() => {
    setBroken(false);
  }, [resolved]);

  if (!resolved || broken) {
    return (
      <div
        className={`w-full ${minHeight} ${rounded} flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-900/40 border border-dashed border-slate-300 dark:border-slate-700 text-slate-400 ${className}`}
        data-testid="creative-image-placeholder"
      >
        <ImageOff size={28} className="mb-1.5 opacity-60" />
        <span className="text-[11px] font-medium">
          {!resolved ? 'No creative' : 'Image unavailable'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${minHeight} ${rounded} overflow-hidden bg-slate-100 dark:bg-slate-900/30 flex items-center justify-center ${className}`}
    >
      <img
        src={resolved}
        alt={alt}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>
  );
};

export default CreativeImage;
